/**
 * User location — split in two on purpose: one hook READS, one hook ASKS.
 *
 * This file used to do both in a `useEffect` that ran on mount, so the very
 * first thing Explore did was raise the system permission dialog, before it had
 * shown a single car. Denying it fell back to a hardcoded San Salvador, which
 * inverted the whole product: because every vehicle is in San Salvador, DENYING
 * the permission gave you a working app and GRANTING it could give you an empty
 * one. Anyone opening Tago from outside the country — an App Store reviewer, a
 * Salvadoran abroad planning a trip home — got a screen with no results, no map
 * pins and no explanation.
 *
 * Now:
 *   useLocation()         reads what we already know. Returns null when we do
 *                         not know. Never asks, never invents a city.
 *   useRequestLocation()  asks, and only the "Cerca de mí" control calls it.
 *
 * `null` is a real answer and callers must handle it. The invented default was
 * worse than not knowing, because "San Salvador" and "we have no idea" looked
 * identical to every consumer downstream.
 *
 * City names come from Mapbox Reverse Geocoding (expo's reverseGeocodeAsync was
 * removed in SDK 49).
 */
import { useCallback, useState } from 'react'
import * as Location from 'expo-location'
import { env } from '../env'
import { fetchWithTimeout } from '../utils/fetch-with-timeout'
import { useExploreStore } from '../stores/explore.store'
import type { UserLocation } from '../types/vehicle.types'

const MAPBOX_TOKEN = env.EXPO_PUBLIC_MAPBOX_TOKEN ?? ''

/** What a permission request ended up meaning for the caller. */
export type LocationRequestOutcome =
  | { status: 'granted'; location: UserLocation }
  /** The person said no, or the OS said no on their behalf. */
  | { status: 'denied'; canAskAgain: boolean }
  /** Permission was fine; getting a fix failed. Worth retrying, unlike denial. */
  | { status: 'unavailable' }

async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  if (!MAPBOX_TOKEN) return null
  try {
    const url = `https://api.mapbox.com/search/geocode/v6/reverse?longitude=${lng}&latitude=${lat}&types=place&language=es&limit=1&access_token=${MAPBOX_TOKEN}`
    const res = await fetchWithTimeout(url, undefined, { timeoutMs: 8_000 })
    if (!res.ok) return null
    const json = await res.json()
    const feature = json?.features?.[0]
    return feature?.properties?.name ?? feature?.properties?.full_address ?? null
  } catch {
    return null
  }
}

/**
 * The location we already have, or `null`.
 *
 * Deliberately has no effect and no loading state: nothing here can start a
 * permission prompt, so no screen can raise one by accident just by rendering.
 */
export function useLocation(): { location: UserLocation | null } {
  const location = useExploreStore((s) => s.location)
  return { location }
}

/**
 * Ask for location, once, because the person asked for something that needs it.
 *
 * Reports `canAskAgain` on denial so the caller can tell the two denials apart:
 * the first one shows a dialog, and every one after it returns instantly with
 * no dialog at all. Without that distinction the second tap looks like a dead
 * button, and the honest response is to offer Settings instead.
 */
export function useRequestLocation() {
  const setLocation = useExploreStore((s) => s.setLocation)
  const [isRequesting, setIsRequesting] = useState(false)

  const request = useCallback(async (): Promise<LocationRequestOutcome> => {
    setIsRequesting(true)
    try {
      const { status, canAskAgain } = await Location.requestForegroundPermissionsAsync()
      if (status !== 'granted') return { status: 'denied', canAskAgain }

      const pos = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.Balanced,
      })
      const name = await reverseGeocode(pos.coords.latitude, pos.coords.longitude)
      const location: UserLocation = {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        name,
      }
      setLocation(location)
      return { status: 'granted', location }
    } catch {
      return { status: 'unavailable' }
    } finally {
      setIsRequesting(false)
    }
  }, [setLocation])

  return { request, isRequesting }
}

/**
 * Read the permission we already hold, WITHOUT prompting.
 *
 * `getForegroundPermissionsAsync` never shows a dialog, which is what makes it
 * safe to call on startup. It exists so a persisted "Cerca de mí" can be
 * honoured on the next launch — or quietly dropped when the person revoked the
 * permission in Settings, or granted it just once.
 */
export async function hasForegroundLocationPermission(): Promise<boolean> {
  try {
    const { status } = await Location.getForegroundPermissionsAsync()
    return status === 'granted'
  } catch {
    return false
  }
}

/** The current position, assuming permission is already held. */
export async function readCurrentLocation(): Promise<UserLocation | null> {
  try {
    const pos = await Location.getCurrentPositionAsync({
      accuracy: Location.Accuracy.Balanced,
    })
    const name = await reverseGeocode(pos.coords.latitude, pos.coords.longitude)
    return { lat: pos.coords.latitude, lng: pos.coords.longitude, name }
  } catch {
    return null
  }
}
